import { Grid, Form, Button, Divider, Loader } from "semantic-ui-react";
import BCFormInput from "../ui/BCFormInput";
import { getDropdownOptions, validateObj, validateEmails, handleInputType, handleAddressValue, guid } from "../../Helpers";
import { ErrorMessages, GlobalConstants } from "../constants/GlobalConstants";
import { useState } from "react";
import BCValidationMessage from "../ui/BCValidationMessage";
import { BPQuestionLabels } from "./BPQuestionLabels";

const BPAddGuarantorForm = ({ 
  applicationMetadata, 
  handleBrokerGuarantorInviteAsync, 
  handleApplicationFormChange, 
  handleSaveApplication,
  startAppObject, 
  setStartAppObject,
  setPrimeGuarantorModal,
  setIsApplicationNew,
  clearInviteGuarantorFormInputs,
  handleGetBorrowerEntityAsync,
  newBorrowerEntity,
  setNewBorrowerEntity,
  handleEntityFormChange,
  handleGuarantorSearchChange,
  guarantorSearch,
  handleDoubleFilterGuarantorSuggestions,
  handleGuarantorSelect,
  isBrokerGuarantorSelected,
  handleGuarantorFocus,
  setIsBrokerGuarantorInvited,
  setBrokerGuarantorInvitedName,
  handleCreateOrPatchBorrowerEntity,
  handleCreateOrPatchGuarantor
}) => { 
  const [invalidProps, setInvalidProps] = useState([] as string[]);
  const [isLoadingGuarantorInvitation, setIsLoadingGuarantorInvitation] = useState(false as boolean);
  const [isBorrowerEntitySearchSelected, setIsBorrowerEntitySearchSelected] = useState(false as boolean);
  const [borrowerSearch, setBorrowerSearch] = useState("" as string);
  const [borrowerSuggestions, setBorrowerSuggestions] = useState([] as any[]);
  const [guarantorOrganizationContactId, setGuarantorOrganizationContactId] = useState(guid());

  const handleBorrowerSuggestions = () => {
    const results = [] as any[];
    let resultLimit = 6;
    let filteredSuggestions = applicationMetadata.associatedBorrowers ? applicationMetadata.associatedBorrowers : [];
    if (borrowerSearch !== "") {
      filteredSuggestions = borrowerSuggestions;
    }

    if (filteredSuggestions && filteredSuggestions.length > 0) {
      filteredSuggestions.forEach(associatedBorrower => {
        if (resultLimit > 0) {
          results.push({
            key: associatedBorrower.id,
            title: associatedBorrower.name,
            value: associatedBorrower
          });
        }

        resultLimit--;
      });
    }

    return results;
  };

  const handleBorrowerSelect = async (result) => {
    if (result && result.value) {
      await handleGetBorrowerEntityAsync(startAppObject.id, result.value.id);
      startAppObject.borrowerOrganizationId = result.value.id;
      startAppObject.borrowerOrganizationName = result.value.name;
      setStartAppObject({ ...startAppObject });
      setIsBorrowerEntitySearchSelected(false);
    }
  };

  const handleBorrowerSearchChange = async (value, field, charLimit = undefined as number | undefined) => {
    const borrowerSearchValue = handleInputType("string", value, undefined, undefined, charLimit);
    startAppObject.borrowerOrganizationName = borrowerSearchValue;
    setStartAppObject({ ...startAppObject });
    setBorrowerSearch(borrowerSearchValue);
    setIsBorrowerEntitySearchSelected(true);
    const secondFilteredBorrowers = applicationMetadata.associatedBorrowers;
    if (borrowerSearchValue !== "" && secondFilteredBorrowers && secondFilteredBorrowers.length > 0) {
      const filteredBorrowers = secondFilteredBorrowers.filter(x => (x.name && (x.name).toLowerCase().includes(borrowerSearchValue.toLowerCase())));
      setBorrowerSuggestions(filteredBorrowers);
    }
    else {
      setBorrowerSuggestions([]);
    }
  };

  const handleModalGuarantorSearchChange = (value, field) => {
    handleApplicationFormChange(value, field, startAppObject, setStartAppObject, GlobalConstants.InputTypes.STRING, undefined, 50);
    handleGuarantorSearchChange(value, field);
  };

  const handleModalGuarantorSelect = (result) => {
    if (result && result.value) {
      handleApplicationFormChange(result.value.email, "guarantorEmailAddress", startAppObject, setStartAppObject, GlobalConstants.InputTypes.STRING, undefined, 50);
      startAppObject.guarantorFirstName = result.value.firstName;
      startAppObject.guarantorLastName = result.value.lastName;
      setStartAppObject({ ...startAppObject });
      handleGuarantorSelect(result, GlobalConstants.AccessLevelIds.FULL_EDIT, "Pending");
      setGuarantorOrganizationContactId(result.value.id);
    }
  };
  
  const handleInviteGuarantor = async () => {
    let notValidProps = validateObj(startAppObject, ["name", "loanProgramId", "newStructurePropertyTypeId", "borrowerOrganizationName", "guarantorFirstName", "guarantorLastName", "guarantorEmailAddress"]);
    notValidProps = notValidProps.concat(validateObj(newBorrowerEntity, ["entitySubTypeId", "physicalCityName", "physicalStateName", "physicalZipCode", "physicalStreet"]));
    if (!newBorrowerEntity.id) {
      newBorrowerEntity.id = guid();
    }

    notValidProps = notValidProps.concat(validateEmails(startAppObject, ["guarantorEmailAddress"]));
    if (notValidProps.length === 0) {
      setIsLoadingGuarantorInvitation(true);
      let isApiSuccess = await handleSaveApplication();
      if (isApiSuccess) {
        setIsApplicationNew(false);
        newBorrowerEntity.name = startAppObject.borrowerOrganizationName;
        startAppObject.borrowerOrganizationId = newBorrowerEntity.id;
        const borrowerObj = {
          id: newBorrowerEntity.id,
          name: startAppObject.borrowerOrganizationName,
          entitySubTypeId: newBorrowerEntity.entitySubTypeId,
          physicalCityName: newBorrowerEntity.physicalCityName,
          physicalStateName: newBorrowerEntity.physicalStateName,
          physicalZipCode: newBorrowerEntity.physicalZipCode,
          physicalStreet: newBorrowerEntity.physicalStreet
        };

        isApiSuccess = await handleCreateOrPatchBorrowerEntity(startAppObject, borrowerObj);
      }

      if (isApiSuccess) {
        isApiSuccess = await handleSaveApplication();
      }

      if (isApiSuccess) {
        const guarantorObj = {
          firstName: startAppObject.guarantorFirstName,
          lastName: startAppObject.guarantorLastName,
          email: startAppObject.guarantorEmailAddress,
          borrowerOrganizationId: newBorrowerEntity.id
        };

        isApiSuccess = await handleCreateOrPatchGuarantor(guarantorOrganizationContactId, guarantorObj);
      }
        
      if (isApiSuccess) {
        const guarantorInviteObj = {
          borrowerApplicationId: startAppObject.id,
          borrowerApplicationName: startAppObject.name,
          firstName: startAppObject.guarantorFirstName,
          lastName: startAppObject.guarantorLastName,
          email: startAppObject.guarantorEmailAddress,
          borrowerName: startAppObject.borrowerOrganizationName
        };

        setIsLoadingGuarantorInvitation(true);
        isApiSuccess = await handleBrokerGuarantorInviteAsync(guarantorInviteObj);
      }

      if (isApiSuccess) {
        setIsBrokerGuarantorInvited(true);
        setBrokerGuarantorInvitedName(`${startAppObject.guarantorFirstName} ${startAppObject.guarantorLastName}`);
        setTimeout(() => {
          setIsBrokerGuarantorInvited(false);
          setBrokerGuarantorInvitedName("");
        }, 3000);

        startAppObject.isExistingBorrower = true;
        setStartAppObject({ ...startAppObject });
        setNewBorrowerEntity({ ...newBorrowerEntity });
      }

      setPrimeGuarantorModal(false);
      setIsLoadingGuarantorInvitation(false);
    }
    else {
      setTimeout(() => {
        setInvalidProps(notValidProps);
      }, 50);
    } 
  };

  return <Form className="primary-form" autoComplete="off">
    <Grid>
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            label="Project Name"
            placeholder="Builders Project"
            inputType="text"
            field="name"
            obj={startAppObject}
            handleFormChange={(value, field) => handleApplicationFormChange(value, field, startAppObject, setStartAppObject, GlobalConstants.InputTypes.STRING, undefined, 50)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="name" />
        </Grid.Column>  
      </Grid.Row>    
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput
            labelClass="gmr-background-label"
            placeholder="Please Select"
            type={GlobalConstants.FormInputTypes.DROPDOWN}
            label="Loan Program"
            field="loanProgramId"
            obj={startAppObject}
            dropdownOptions={getDropdownOptions(applicationMetadata.loanPrograms, ["id"], "name")}
            handleFormChange={(value, field) => handleApplicationFormChange(value, field, startAppObject, setStartAppObject, GlobalConstants.InputTypes.STRING, undefined, 50)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="loanProgramId" />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput
            labelClass="gmr-background-label"
            placeholder="Please Select"
            type={GlobalConstants.FormInputTypes.DROPDOWN}
            label={BPQuestionLabels.NewStructure}
            field="newStructurePropertyTypeId"
            obj={startAppObject}
            dropdownOptions={getDropdownOptions(applicationMetadata.propertyTypes, ["id"], "name")}
            handleFormChange={(value, field) => handleApplicationFormChange(value, field, startAppObject, setStartAppObject, GlobalConstants.InputTypes.STRING, undefined, 50)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="newStructurePropertyTypeId" />
        </Grid.Column>
      </Grid.Row>
      <Divider section />
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput
            labelClass="gmr-background-label"
            label="Borrowing Entity Name"
            placeholder="Borrower Name"
            inputKey="entity"
            type={GlobalConstants.FormInputTypes.SEARCH_FILTER}
            onSearchChange={(value, field) => handleBorrowerSearchChange(value, field)}
            value={startAppObject.borrowerOrganizationName}
            renderSuggestions={handleBorrowerSuggestions()}
            handleSearchSelect={(e, data) => handleBorrowerSelect(data.result)}
            isSearchOpen={isBorrowerEntitySearchSelected}
            onSearchFocus={() => setIsBorrowerEntitySearchSelected(true)}
            noSearchResultsMessage="To add a new Borrower, type their name."
            searchClass="bc-search-input-full"
            handleSearchKeyDown={() => void 0}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="borrowerOrganizationName" />
        </Grid.Column>  
      </Grid.Row>
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput 
            labelClass="gmr-background-label"
            type="dropdown"
            inputKey="entity"
            label="Business Type"
            placeholder="Please Select"
            obj={newBorrowerEntity}
            field="entitySubTypeId"
            dropdownOptions={getDropdownOptions(applicationMetadata.entitySubTypes, ["id"], "name")}
            handleFormChange={(value, field) => handleEntityFormChange(value, field)}
            required
          />
          <BCValidationMessage invalidProps={invalidProps} prop="entitySubTypeId" />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
        <BCFormInput
            label="Address"
            inputKey="entity"
            labelClass="gmr-background-label"
            type={GlobalConstants.FormInputTypes.PLACES}
            placeDetailsObject={newBorrowerEntity}
            setPlaceDetailsObject={setNewBorrowerEntity}
            placeholder="1234 Main Street, Seattle, WA 98052, USA"
            defaultValue={handleAddressValue({
              address: newBorrowerEntity.physicalStreet,
              city: newBorrowerEntity.physicalCityName,
              state: newBorrowerEntity.physicalStateName,
              zip: newBorrowerEntity.physicalZip
            })}
            placeFields={["address", "city", "state", "county", "zip"]}
            placeFieldsNameOverrides={[
              {
                addressComponent: "address",
                field: "physicalStreet"
              },
              {
                addressComponent: "city",
                field: "physicalCityName"
              },
              {
                addressComponent: "state",
                field: "physicalStateName"
              },
              {
                addressComponent: "county",
                field: "physicalCountyName"
              },
              {
                addressComponent: "zip",
                field: "physicalZipCode"
              },
            ]}
            required
          />
          {invalidProps.includes("physicalCityName") ? <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalCityName" 
            propText="This city is invalid." 
          /> : invalidProps.includes("physicalStateName") ? <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalStateName"
            propText="This state is invalid."
          /> : invalidProps.includes("physicalZipCode") ? <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalZipCode"
            propText="This zip code is invalid."
          /> : <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="physicalStreet" 
            propText={ErrorMessages.Address} />
          }
        </Grid.Column>  
      </Grid.Row>
      <Divider section />
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput
            tooltip="Add the guarantors for the loan below. Once added they will recieve an email inviting them to review the application and to sign any necessary disclosures." 
            labelClass="gmr-background-label"
            label="Guarantor Email Address"
            placeholder="john.smith@gmail.com"
            type={GlobalConstants.FormInputTypes.SEARCH_FILTER}
            onSearchChange={(value, field) => handleModalGuarantorSearchChange(value, field)}
            field="guarantorEmailAddress"
            value={startAppObject.guarantorEmailAddress}
            renderSuggestions={handleDoubleFilterGuarantorSuggestions(guarantorSearch)}
            handleSearchSelect={(e, data) => handleModalGuarantorSelect(data.result)}
            isSearchOpen={!isBrokerGuarantorSelected}
            onSearchFocus={() => handleGuarantorFocus()}
            noSearchResultsMessage="To add a new Guarantor, type their name."
            searchClass="bc-search-input-full"
            handleSearchKeyDown={() => void 0}
            required
          />
          <BCValidationMessage 
            invalidProps={invalidProps} 
            prop="guarantorEmailAddress"
            propText="This email is invalid."
          />
        </Grid.Column>  
      </Grid.Row>
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            label="Guarantor First Name"
            placeholder="John"
            inputType="text"
            required
            field="guarantorFirstName"
            obj={startAppObject}
            handleFormChange={(value, field) => handleApplicationFormChange(value, field, startAppObject, setStartAppObject, GlobalConstants.InputTypes.STRING, undefined, 50)}
          />
          <BCValidationMessage invalidProps={invalidProps} prop="guarantorFirstName" />
        </Grid.Column>  
      </Grid.Row>
      <Grid.Row className="application-row" columns={1}>
        <Grid.Column>
          <BCFormInput
            labelClass="gmr-background-label"
            type="input"
            label="Guarantor Last Name"
            placeholder="Smith"
            inputType="text"
            required
            field="guarantorLastName"
            obj={startAppObject}
            handleFormChange={(value, field) => handleApplicationFormChange(value, field, startAppObject, setStartAppObject, GlobalConstants.InputTypes.STRING, undefined, 50)}
          />
          <BCValidationMessage invalidProps={invalidProps} prop="guarantorLastName" />
        </Grid.Column>  
      </Grid.Row>
      <Grid.Row columns={2}>
        <Grid.Column>
          <Button icon fluid onClick={() => clearInviteGuarantorFormInputs() } >
            <div className="gmr-button-container">
              <div className="gmr-button-text">Cancel</div>
            </div>
          </Button>
        </Grid.Column>
        <Grid.Column>
          <Button 
            className="bp-button" 
            fluid 
            onClick={() => handleInviteGuarantor()}
          >
            <div className="gmr-button-container">
              <div className="gmr-button-text">Send Invitation</div>
              <div className="gmr-button-text">{ isLoadingGuarantorInvitation ? <Loader active inline="centered" size={"small"} /> : ""}</div>
            </div>
          </Button>
        </Grid.Column>
      </Grid.Row>
    </Grid>
  </Form>;
};

export default BPAddGuarantorForm;